import { useState, useEffect } from 'react'
import { BookOpen, Star, Search } from 'lucide-react'

interface BooksWidgetProps {
  widget: any
  isExpanded: boolean
}

interface Book {
  id: string
  title: string
  author: string
  cover: string
  rating: number
  description: string
  genre: string[]
  publishedDate: string
  pageCount: number
}

export default function BooksWidget({ widget, isExpanded }: BooksWidgetProps) {
  const [books, setBooks] = useState<Book[]>([])
  const [selectedGenre, setSelectedGenre] = useState<string>('fiction')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedBook, setSelectedBook] = useState<Book | null>(null)

  const genres = [
    'fiction',
    'non-fiction',
    'mystery',
    'thriller',
    'romance',
    'sci-fi',
    'fantasy',
    'biography',
    'history',
    'self-help'
  ]

  // Mock data - Replace with Google Books API integration
  useEffect(() => {
    const mockBooks: Book[] = [
      {
        id: '1',
        title: 'The Midnight Library',
        author: 'Matt Haig',
        cover: 'https://images-na.ssl-images-amazon.com/images/S/compressed.photo.goodreads.com/books/1602190253i/52578297.jpg',
        rating: 4.2,
        description: 'Between life and death there is a library, and within that library, the shelves go on forever.',
        genre: ['fiction', 'fantasy'],
        publishedDate: '2020-08-13',
        pageCount: 304
      },
      {
        id: '2',
        title: 'Atomic Habits',
        author: 'James Clear',
        cover: 'https://images-na.ssl-images-amazon.com/images/S/compressed.photo.goodreads.com/books/1655988385i/40121378.jpg',
        rating: 4.5,
        description: 'Tiny changes, remarkable results. Transform your life with the power of atomic habits.',
        genre: ['self-help', 'non-fiction'],
        publishedDate: '2018-10-16',
        pageCount: 320
      },
      {
        id: '3',
        title: 'Project Hail Mary',
        author: 'Andy Weir',
        cover: 'https://images-na.ssl-images-amazon.com/images/S/compressed.photo.goodreads.com/books/1597695864i/54493401.jpg',
        rating: 4.7,
        description: 'A lone astronaut must save the earth from disaster in this incredible new science-based thriller.',
        genre: ['sci-fi', 'fiction'],
        publishedDate: '2021-05-04',
        pageCount: 476
      },
      {
        id: '4',
        title: 'Where the Crawdads Sing',
        author: 'Delia Owens',
        cover: 'https://images-na.ssl-images-amazon.com/images/S/compressed.photo.goodreads.com/books/1582135294i/36809135.jpg',
        rating: 4.4,
        description: 'A mysterious and unforgettable story of the resilient young woman who raised herself in the marshes.',
        genre: ['fiction', 'mystery'],
        publishedDate: '2018-08-14',
        pageCount: 384
      }
    ]

    setBooks(mockBooks)
  }, [])

  const filteredBooks = books.filter(book => {
    const matchesGenre = book.genre.includes(selectedGenre)
    const matchesSearch = searchQuery.length < 2 || 
      book.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      book.author.toLowerCase().includes(searchQuery.toLowerCase())
    return matchesGenre && matchesSearch
  })

  const renderCompactView = () => (
    <div className="space-y-3">
      <div className="flex gap-2 mb-3 overflow-x-auto">
        {genres.slice(0, 4).map((genre) => (
          <button
            key={genre}
            onClick={() => setSelectedGenre(genre)}
            className={`px-3 py-1 rounded-full text-xs whitespace-nowrap transition ${
              selectedGenre === genre
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {genre.charAt(0).toUpperCase() + genre.slice(1)}
          </button>
        ))}
      </div>

      {filteredBooks.slice(0, 3).map((book) => (
        <div
          key={book.id}
          className="flex items-start gap-3 p-2 hover:bg-gray-50 rounded-lg transition cursor-pointer"
          onClick={() => isExpanded && setSelectedBook(book)}
        >
          <img
            src={book.cover}
            alt={book.title}
            className="w-12 h-16 object-cover rounded shadow"
          />
          <div className="flex-1 min-w-0">
            <div className="font-medium text-gray-900 text-sm mb-1 line-clamp-2">{book.title}</div>
            <div className="text-xs text-gray-600 mb-1">{book.author}</div>
            <div className="flex items-center gap-1">
              <Star size={12} className="text-yellow-500 fill-current" />
              <span className="text-xs text-gray-600">{book.rating}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  )

  const renderExpandedView = () => (
    <div className="h-full flex gap-4">
      {/* Sidebar */}
      <div className="w-64 overflow-y-auto pr-4">
        {/* Search */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search books..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Genres */}
        <div>
          <h3 className="font-semibold text-gray-900 mb-3 text-sm">Genres</h3>
          <div className="space-y-1">
            {genres.map((genre) => (
              <button
                key={genre}
                onClick={() => setSelectedGenre(genre)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition ${
                  selectedGenre === genre
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {genre.charAt(0).toUpperCase() + genre.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Books Grid */}
      <div className="flex-1 overflow-y-auto">
        <h2 className="text-xl font-bold text-gray-900 mb-4 capitalize">
          {selectedGenre} Books
        </h2>

        <div className="grid grid-cols-4 gap-4">
          {filteredBooks.map((book) => (
            <button
              key={book.id}
              onClick={() => setSelectedBook(book)}
              className={`text-left group ${
                selectedBook?.id === book.id ? 'ring-2 ring-primary-500 rounded-lg' : ''
              }`}
            >
              <div className="relative mb-2 rounded-lg overflow-hidden">
                <img
                  src={book.cover}
                  alt={book.title}
                  className="w-full aspect-[2/3] object-cover group-hover:scale-105 transition duration-300"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition" />
              </div>
              <h3 className="font-medium text-gray-900 text-sm mb-1 line-clamp-2">
                {book.title}
              </h3>
              <p className="text-xs text-gray-600 mb-1">{book.author}</p>
              <div className="flex items-center gap-1">
                <Star size={12} className="text-yellow-500 fill-current" />
                <span className="text-xs text-gray-600">{book.rating}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Book Detail */}
      {selectedBook && (
        <div className="w-80 overflow-y-auto border-l border-gray-200 pl-4">
          <div className="mb-4">
            <img
              src={selectedBook.cover}
              alt={selectedBook.title}
              className="w-full aspect-[2/3] object-cover rounded-lg shadow-lg"
            />
          </div>

          <div className="space-y-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-1">{selectedBook.title}</h2>
              <p className="text-gray-600 mb-2">{selectedBook.author}</p>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1">
                  <Star size={16} className="text-yellow-500 fill-current" />
                  <span className="font-semibold text-gray-900">{selectedBook.rating}</span>
                </div>
                <span className="text-sm text-gray-500">{selectedBook.pageCount} pages</span>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2 text-sm">Genres</h3>
              <div className="flex flex-wrap gap-2">
                {selectedBook.genre.map((g) => (
                  <span
                    key={g}
                    className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
                  >
                    {g.charAt(0).toUpperCase() + g.slice(1)}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2 text-sm">Description</h3>
              <p className="text-gray-600 text-sm">{selectedBook.description}</p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2 text-sm">Published</h3>
              <p className="text-gray-600 text-sm">
                {new Date(selectedBook.publishedDate).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>

            <div className="flex gap-2">
              <button className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition flex items-center justify-center gap-2 text-sm">
                <BookOpen size={16} />
                Read Now
              </button>
              <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition text-sm">
                Add to List
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 overflow-hidden">
        {isExpanded ? renderExpandedView() : renderCompactView()}
      </div>
    </div>
  )
}




